import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, TrendingUp, TrendingDown, Wallet, Receipt, Users, LineChart, Settings2, Check } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import { useCompany } from './CompanyProvider';
import { dashboardApi, elsterApi, type DashboardData, type TaxDeadlineItem, type ElsterCertificate } from '../lib/api';
import { Button, Card, euro } from './ui';

const DAY_MS = 86_400_000;

/** Verbleibende Tage bis zu einem ISO-Datum (auf Tagesgrenze gerundet). */
function daysUntil(iso: string): number {
  const target = new Date(iso);
  const now = new Date();
  const a = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / DAY_MS);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function deadlineTone(days: number): { wrap: string; badge: string } {
  if (days <= 7) return { wrap: 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30', badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' };
  if (days <= 30) return { wrap: 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' };
  return { wrap: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800', badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
}

function DeadlineRow({ d }: { d: TaxDeadlineItem }): ReactElement {
  const days = daysUntil(d.date);
  const tone = deadlineTone(days);
  const countdown = days < 0 ? 'überfällig' : days === 0 ? 'heute fällig' : days === 1 ? 'morgen' : `in ${days} Tagen`;
  return (
    <div className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${tone.wrap}`}>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{d.label}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">Fällig am {formatDate(d.date)}</div>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${days < 0 ? 'bg-red-600 text-white' : tone.badge}`}>
        {countdown}
      </span>
    </div>
  );
}

type Period = 'year' | 'lastYear' | 'month';

/** Reines SVG-Balkendiagramm: je Bucket Einnahmen (blau) + Ausgaben (rot).
 *  Buckets richten sich nach der Periode: Jahr/Vorjahr = Monate, Monat = Tage. */
function IncomeExpenseChart({ data, period }: { data: DashboardData; period: Period }): ReactElement {
  const series = period === 'year' ? data.chartYear : period === 'lastYear' ? data.chartLastYear : data.chartMonth;
  const max = Math.max(1, ...series.flatMap((m) => [m.incomeCents, m.expenseCents]));
  const empty = series.every((m) => m.incomeCents === 0 && m.expenseCents === 0);
  const heading =
    period === 'year' ? `Einnahmen & Ausgaben ${data.thisYear.year}`
    : period === 'lastYear' ? `Einnahmen & Ausgaben ${data.lastYear.year}`
    : 'Einnahmen & Ausgaben (Monat)';
  const emptyHint =
    period === 'month' ? 'Noch keine Daten in diesem Monat.' : 'Noch keine Daten in diesem Jahr.';
  // Bei Tagesansicht (viele Balken) nur jede n-te Beschriftung zeigen (lesbar).
  const labelEvery = period === 'month' ? (series.length > 20 ? 5 : 2) : 1;

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{heading}</h2>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-500" />Einnahmen</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-rose-400" />Ausgaben</span>
        </div>
      </div>
      {empty ? (
        <p className="py-8 text-center text-sm text-gray-400">{emptyHint}</p>
      ) : (
        <div className="flex h-44 items-end justify-between gap-px sm:gap-1">
          {series.map((m, i) => (
            <div key={m.key} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
              <div className="flex h-36 w-full items-end justify-center gap-0.5 sm:gap-1">
                <div
                  className="w-1/2 max-w-[16px] rounded-t bg-blue-500 transition-all"
                  style={{ height: `${(m.incomeCents / max) * 100}%` }}
                  title={`${m.label}: Einnahmen ${euro(m.incomeCents)}`}
                />
                <div
                  className="w-1/2 max-w-[16px] rounded-t bg-rose-400 transition-all"
                  style={{ height: `${(m.expenseCents / max) * 100}%` }}
                  title={`${m.label}: Ausgaben ${euro(m.expenseCents)}`}
                />
              </div>
              <span className="h-3 text-[10px] leading-3 text-gray-500 dark:text-gray-400">
                {i % labelEvery === 0 ? m.label : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function PeriodOverview({ data, period, setPeriod }: { data: DashboardData; period: Period; setPeriod: (p: Period) => void }): ReactElement {
  const m = period === 'year' ? data.thisYear : period === 'lastYear' ? data.lastYear : data.thisMonth;
  const positive = m.resultCents >= 0;
  const rows = [
    { label: 'Einnahmen', value: euro(m.incomeCents), icon: TrendingUp, color: 'text-blue-600 dark:text-blue-400' },
    { label: 'Ausgaben', value: euro(m.expenseCents), icon: TrendingDown, color: 'text-rose-500 dark:text-rose-400' },
  ];
  const tab = (key: Period, label: string) => (
    <button
      type="button"
      onClick={() => setPeriod(key)}
      className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${
        period === key
          ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );
  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Übersicht</h2>
        <div className="flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-900">
          {tab('year', String(data.thisYear.year))}
          {tab('lastYear', String(data.lastYear.year))}
          {tab('month', 'Monat')}
        </div>
      </div>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <r.icon className={`h-4 w-4 ${r.color}`} />{r.label}
            </span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{r.value}</span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
            <Wallet className="h-4 w-4 text-gray-400" />Ergebnis
          </span>
          <span className={`text-lg font-bold ${positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {positive ? '+' : ''}{euro(m.resultCents)}
          </span>
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-400">
        {period === 'year' ? 'Laufendes Jahr. ' : period === 'lastYear' ? `Vorjahr ${data.lastYear.year}. ` : 'Laufender Monat. '}
        Einnahmen aus bezahlten Rechnungen und Einnahme-Belegen, Ausgaben aus erfassten Belegen ({data.kleinunternehmer.isKleinunternehmer ? 'brutto – Kleinunternehmer §19' : 'netto'}, Zuflussprinzip).
      </p>
    </Card>
  );
}

/** Steuervorschau: voraussichtliche USt-Zahllast des laufenden Quartals. */
function TaxPreview({ data }: { data: DashboardData }): ReactElement {
  const v = data.ustVorschau;
  if (!v.applicable) {
    return (
      <Card>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Receipt className="h-4 w-4 text-gray-400" />Steuervorschau
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Als Kleinunternehmer (§19 UStG) führst du keine Umsatzsteuer ab – daher keine Zahllast.
        </p>
      </Card>
    );
  }
  const owe = v.zahllastCents >= 0;
  return (
    <Card>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <Receipt className="h-4 w-4 text-gray-400" />Steuervorschau · Q{v.quarter}/{v.year}
      </h2>
      <div className="space-y-2.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-300">Vereinnahmte USt</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{euro(v.ustCents)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-300">– Vorsteuer</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{euro(v.vorsteuerCents)}</span>
        </div>
        <div className="flex items-center justify-between border-t border-gray-200 pt-2.5 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {owe ? 'Voraussichtliche Zahllast' : 'Voraussichtliches Guthaben'}
          </span>
          <span className={`text-lg font-bold ${owe ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {euro(Math.abs(v.zahllastCents))}
          </span>
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-400">
        Schätzung für das laufende Quartal nach Ist-Versteuerung (Zuflussprinzip). Ohne Gewähr – maßgeblich ist deine UStVA.
      </p>
    </Card>
  );
}

/** Liquiditätsplanung: erwarteter Kontostand über die nächsten Wochen. */
function LiquidityPlan({ data }: { data: DashboardData }): ReactElement {
  const weeks = data.liquidity.weeks;
  const values = [data.liquidity.startBalanceCents, ...weeks.map((w) => w.balanceCents)];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  // Endsaldo + ob er irgendwann negativ wird.
  const endBalance = weeks.length ? weeks[weeks.length - 1]!.balanceCents : data.liquidity.startBalanceCents;
  const goesNegative = values.some((v) => v < 0);
  // Höhe relativ zur Spanne (0 unten .. 100 oben). Nulllinie für Optik.
  const h = (c: number) => `${Math.round(((c - min) / span) * 100)}%`;

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <LineChart className="h-4 w-4 text-gray-400" />Liquiditätsplanung
        </h2>
        <span className="text-xs text-gray-500 dark:text-gray-400">nächste {weeks.length} Wochen</span>
      </div>
      <div className="mb-3 flex items-end justify-between gap-1">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Aktueller Kontostand</div>
          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{euro(data.liquidity.startBalanceCents)}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500 dark:text-gray-400">Erwartet in {weeks.length} Wochen</div>
          <div className={`text-lg font-bold ${endBalance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {euro(endBalance)}
          </div>
        </div>
      </div>
      {weeks.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-400">Keine offenen Posten für eine Prognose.</p>
      ) : (
        <div className="flex h-28 items-end justify-between gap-1">
          {weeks.map((w) => (
            <div key={w.key} className="flex min-w-0 flex-1 flex-col items-center gap-1">
              <div className="flex h-20 w-full items-end justify-center">
                <div
                  className={`w-full max-w-[20px] rounded-t transition-all ${w.balanceCents < 0 ? 'bg-rose-400' : 'bg-emerald-400'}`}
                  style={{ height: h(w.balanceCents) }}
                  title={`${w.label}: ${euro(w.balanceCents)} (Eingänge ${euro(w.inCents)}, Ausgänge ${euro(w.outCents)})`}
                />
              </div>
              <span className="h-3 text-[10px] leading-3 text-gray-500 dark:text-gray-400">{w.label}</span>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-gray-400">
        {goesNegative
          ? '⚠️ Der Kontostand könnte negativ werden. Prüfe offene Forderungen und anstehende Zahlungen.'
          : 'Prognose aus Banksaldo + offenen Forderungen (nach Fälligkeit). Ohne Gewähr.'}
      </p>
    </Card>
  );
}

/** Top-Kunden nach Umsatz (laufendes Jahr). */
function TopCustomers({ data }: { data: DashboardData }): ReactElement {
  const list = data.topCustomers;
  const max = Math.max(1, ...list.map((c) => c.revenueCents));
  return (
    <Card>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <Users className="h-4 w-4 text-gray-400" />Top-Kunden {data.thisYear.year}
      </h2>
      {list.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-400">Noch keine bezahlten Rechnungen in diesem Jahr.</p>
      ) : (
        <div className="space-y-2.5">
          {list.map((c, i) => (
            <div key={`${c.name}-${i}`}>
              <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                <span className="truncate text-gray-700 dark:text-gray-200">{i + 1}. {c.name}</span>
                <span className="shrink-0 font-medium text-gray-900 dark:text-gray-100">{euro(c.revenueCents)}</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div className="h-full rounded-full bg-blue-500" style={{ width: `${(c.revenueCents / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Kundenwachstum + durchschnittlicher Rechnungsbetrag (zwei kompakte Kennzahlen). */
function CustomerStats({ data }: { data: DashboardData }): ReactElement {
  const g = data.customerGrowth;
  const up = g.deltaPercent >= 0;
  return (
    <Card>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <TrendingUp className="h-4 w-4 text-gray-400" />Kennzahlen
      </h2>
      <div className="space-y-4">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Neue Kunden {data.thisYear.year}</div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{g.thisYear}</span>
            <span className={`text-xs font-medium ${up ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {up ? '▲' : '▼'} {Math.abs(g.deltaPercent)}% ggü. Vorjahr ({g.lastYear})
            </span>
          </div>
        </div>
        <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">Ø Rechnungsbetrag {data.thisYear.year}</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{euro(data.avgInvoiceCents)}</div>
          <div className="mt-0.5 text-xs text-gray-400">netto, bezahlte Rechnungen</div>
        </div>
      </div>
    </Card>
  );
}

// --- Widget-Registry + Persistenz ------------------------------------------
type WidgetId =
  | 'kpis' | 'chart' | 'overview' | 'taxPreview' | 'liquidity'
  | 'topCustomers' | 'customerStats' | 'kuWarning' | 'elsterCertWarning' | 'deadlines';

interface WidgetDef {
  id: WidgetId;
  label: string;
  /** Breite im Grid: 'full' = ganze Breite, 'two' = 2/3, 'one' = 1/3. */
  span: 'full' | 'two' | 'one';
}

// Reihenfolge = Anzeigereihenfolge. KPIs + Chart/Übersicht standardmäßig an.
const WIDGETS: WidgetDef[] = [
  { id: 'kpis', label: 'Kennzahlen-Karten', span: 'full' },
  { id: 'chart', label: 'Einnahmen/Ausgaben-Diagramm', span: 'two' },
  { id: 'overview', label: 'Perioden-Übersicht', span: 'one' },
  { id: 'taxPreview', label: 'Steuervorschau', span: 'one' },
  { id: 'liquidity', label: 'Liquiditätsplanung', span: 'two' },
  { id: 'topCustomers', label: 'Top-Kunden', span: 'one' },
  { id: 'customerStats', label: 'Kunden-Kennzahlen', span: 'one' },
  { id: 'kuWarning', label: 'Kleinunternehmer-Warnung', span: 'full' },
  { id: 'elsterCertWarning', label: 'ELSTER-Zertifikat-Ablauf', span: 'full' },
  { id: 'deadlines', label: 'Steuer-Fristen', span: 'full' },
];

const DEFAULT_ENABLED: WidgetId[] = ['kpis', 'chart', 'overview', 'kuWarning', 'elsterCertWarning', 'deadlines'];
const STORAGE_PREFIX = 'saldify.dashboard.widgets.';

/** Dashboard-Warnung: ELSTER-Zertifikat laeuft in <30 Tagen ab (oder ist abgelaufen). */
function ElsterCertWarning({ companyId }: { companyId: string }): ReactElement | null {
  const [certs, setCerts] = useState<ElsterCertificate[] | null>(null);
  useEffect(() => {
    let alive = true;
    elsterApi.listCertificates(companyId)
      .then((r) => { if (alive) setCerts(r.certificates); })
      .catch(() => { if (alive) setCerts([]); });
    return () => { alive = false; };
  }, [companyId]);

  if (!certs || certs.length === 0) return null;
  // Zertifikate mit Ablaufdatum, die in <30 Tagen ablaufen oder abgelaufen sind.
  const critical = certs
    .filter((c) => c.validUntil)
    .map((c) => ({ cert: c, days: daysUntil(c.validUntil as string) }))
    .filter((x) => x.days < 30)
    .sort((a, b) => a.days - b.days);
  if (critical.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30">
      <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
        ⚠️ ELSTER-Zertifikat läuft ab
      </h2>
      {critical.map(({ cert, days }) => (
        <p key={cert.id} className="text-sm text-amber-800 dark:text-amber-300/90">
          {days < 0 ? (
            <>Das Zertifikat <strong>{cert.label}</strong> ist seit {Math.abs(days)} Tag(en) <strong>abgelaufen</strong>.</>
          ) : (
            <>Das Zertifikat <strong>{cert.label}</strong> läuft in <strong>{days} Tag(en)</strong> ab (gültig bis {new Date(cert.validUntil as string).toLocaleDateString('de-DE')}).</>
          )}
        </p>
      ))}
      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400/80">
        Erneuern Sie Ihr ELSTER-Zertifikat rechtzeitig im ELSTER-Portal und laden Sie es unter{' '}
        <Link to="/app/einstellungen" className="underline">Einstellungen → ELSTER</Link> neu hoch, damit Übermittlungen weiter möglich bleiben.
      </p>
    </Card>
  );
}

function loadEnabled(companyId: string): WidgetId[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + companyId);
    if (!raw) return DEFAULT_ENABLED;
    const parsed = JSON.parse(raw) as string[];
    const valid = parsed.filter((id): id is WidgetId => WIDGETS.some((w) => w.id === id));
    return valid.length ? valid : DEFAULT_ENABLED;
  } catch {
    return DEFAULT_ENABLED;
  }
}

const SPAN_CLASS: Record<WidgetDef['span'], string> = {
  full: 'lg:col-span-3',
  two: 'lg:col-span-2',
  one: 'lg:col-span-1',
};

export default function Dashboard(): ReactElement {
  const { user } = useAuth();
  const { active } = useCompany();
  const [data, setData] = useState<DashboardData | null>(null);
  // Periode wird hochgezogen, damit Chart UND Uebersicht denselben Zeitraum zeigen.
  const [period, setPeriod] = useState<Period>('year');
  const [enabled, setEnabled] = useState<WidgetId[]>(DEFAULT_ENABLED);
  const [customizing, setCustomizing] = useState(false);
  const firstName = user?.name?.split(' ')[0] ?? '';

  useEffect(() => {
    if (active) {
      dashboardApi.get(active.id).then(setData).catch(() => setData(null));
      setEnabled(loadEnabled(active.id));
    }
  }, [active]);

  function toggleWidget(id: WidgetId) {
    setEnabled((prev) => {
      const next = prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id];
      if (active) localStorage.setItem(STORAGE_PREFIX + active.id, JSON.stringify(next));
      return next;
    });
  }

  const cards = data
    ? [
        { title: 'Offene Rechnungen', value: euro(data.openInvoices.sumCents), hint: `${data.openInvoices.count} Stück` },
        { title: 'Umsatz (Monat)', value: euro(data.revenueThisMonthCents), hint: 'bezahlt, netto' },
        { title: 'Überfällig', value: String(data.overdueCount), hint: 'Rechnungen' },
        { title: 'Belege (Monat)', value: String(data.receiptsThisMonth), hint: 'erfasst' },
      ]
    : [];

  // Ein Widget rendern (oder null, wenn keine Daten / nicht anwendbar).
  function renderWidget(id: WidgetId): ReactElement | null {
    if (!data) return null;
    switch (id) {
      case 'kpis':
        return (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((c) => (
              <Card key={c.title}>
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{c.title}</div>
                <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{c.value}</div>
                <div className="mt-1 text-xs text-gray-400">{c.hint}</div>
              </Card>
            ))}
          </div>
        );
      case 'chart':
        return <IncomeExpenseChart data={data} period={period} />;
      case 'overview':
        return <PeriodOverview data={data} period={period} setPeriod={setPeriod} />;
      case 'taxPreview':
        return <TaxPreview data={data} />;
      case 'liquidity':
        return <LiquidityPlan data={data} />;
      case 'topCustomers':
        return <TopCustomers data={data} />;
      case 'customerStats':
        return <CustomerStats data={data} />;
      case 'kuWarning':
        return data.kleinunternehmer.warnung ? (
          <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30">
            <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
              ⚠️ Kleinunternehmer-Grenze rückt näher
            </h2>
            <p className="text-sm text-amber-800 dark:text-amber-300/90">
              Dein Umsatz {data.kleinunternehmer.year} liegt bei <strong>{euro(data.kleinunternehmer.laufendUmsatzCents)}</strong> –
              das sind <strong>{data.kleinunternehmer.auslastungProzent} %</strong> der Grenze von {euro(data.kleinunternehmer.grenzeCents)} (§19 UStG).
            </p>
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400/80">
              Bei Überschreiten wirst du umsatzsteuerpflichtig. Plane den Wechsel zur Regelbesteuerung ein – Saldify stellt Rechnungen dann automatisch mit USt.
            </p>
          </Card>
        ) : null;
      case 'elsterCertWarning':
        return active ? <ElsterCertWarning companyId={active.id} /> : null;
      case 'deadlines':
        return data.taxDeadlines.length > 0 ? (
          <Card>
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <CalendarClock className="h-4 w-4 text-gray-400" />Anstehende Steuer-Fristen
            </h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {data.taxDeadlines.map((d) => (
                <DeadlineRow key={`${d.kind}-${d.date}`} d={d} />
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-400">
              Richtwerte ohne Dauerfristverlängerung. Maßgeblich ist immer dein Finanzamt / Steuerberater.
            </p>
          </Card>
        ) : null;
      default:
        return null;
    }
  }

  // Aktive Widgets in definierter Reihenfolge, mit ihrer Grid-Breite.
  const activeWidgets = useMemo(
    () => WIDGETS.filter((w) => enabled.includes(w.id)),
    [enabled],
  );

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Hallo{firstName ? ` ${firstName}` : ''} 👋</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {active ? <>Firma: <strong>{active.name}</strong></> : 'Keine Firma ausgewählt.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setCustomizing((v) => !v)}>
            <Settings2 className="mr-1.5 inline h-4 w-4" />Anpassen
          </Button>
          <Link to="/app/rechnungen/neu">
            <Button>+ Neue Rechnung</Button>
          </Link>
        </div>
      </div>

      {customizing && (
        <Card className="mb-4">
          <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Dashboard anpassen</h2>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Wähle aus, welche Widgets angezeigt werden. Deine Auswahl wird pro Firma gespeichert.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {WIDGETS.map((w) => {
              const on = enabled.includes(w.id);
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => toggleWidget(w.id)}
                  className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition ${
                    on
                      ? 'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  <span>{w.label}</span>
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                    on ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {on && <Check className="h-3.5 w-3.5" />}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Widget-Grid: jedes Widget nimmt seine definierte Breite ein. */}
      <div className="grid gap-4 lg:grid-cols-3">
        {activeWidgets.map((w) => {
          const node = renderWidget(w.id);
          if (!node) return null;
          return (
            <div key={w.id} className={SPAN_CLASS[w.span]}>
              {node}
            </div>
          );
        })}
      </div>

      {activeWidgets.length === 0 && (
        <Card className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Keine Widgets ausgewählt. Klicke auf <strong>Anpassen</strong>, um welche hinzuzufügen.
          </p>
        </Card>
      )}
    </div>
  );
}
